import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Wallet } from 'lucide-react';
import { Cartao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { get } from '../../nucleo/api';
import { Barra, BotaoExportar, LinhaEscopo, Rolagem, TD, TH, usePeriodo, Valor } from './comum';
import type { Folha as FolhaApi } from './tipos';

const VALORES = ['proventos_centavos', 'descontos_centavos', 'encargos_centavos', 'liquido_centavos'] as const;

/** E1 · Folha por competência no período: totais de cada competência (sem linha por funcionário; exige folha.ver). */
export function Folha() {
  const { t } = useTranslation('relatorios');
  const { periodo, seletor } = usePeriodo();
  const params = { de: periodo.de, ate: periodo.ate };
  const q = useQuery({ queryKey: ['/relatorios/folha', params], queryFn: () => get<FolhaApi>('/relatorios/folha', params), placeholderData: keepPreviousData });
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <Barra>
        {seletor}
        <BotaoExportar relatorio="folha" parametros={params} />
      </Barra>
      <Estado consulta={q}>
        {(d) => (
          <>
            <LinhaEscopo escopo={d.escopo} />
            {d.competencias.length === 0 ? (
              <Cartao>
                <Vazio icone={<Wallet className="size-5" aria-hidden />} titulo={t('folha.vazioTitulo')} texto={t('folha.vazioTexto')} />
              </Cartao>
            ) : (
              <Cartao semPadding>
                <Rolagem rotulo={t('comum.tabela')}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-borda text-left text-tinta-3">
                        <th scope="col" className={TH}>{t('folha.competencia')}</th>
                        {d.escopo.modo === 'consolidado' && <th scope="col" className={TH}>{t('folha.empresa')}</th>}
                        <th scope="col" className={TH}>{t('folha.estado')}</th>
                        <th scope="col" className={`${TH} text-right`}>{t('folha.funcionarios')}</th>
                        {VALORES.map((k) => (
                          <th key={k} scope="col" className={`${TH} text-right`}>{t(`folha.colunas.${k}`)}</th>
                        ))}
                        <th scope="col" className={`${TH} text-right`}>{t('folha.colunas.custo_centavos')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.competencias.map((c) => (
                        <tr key={c.competencia_id} className="border-b border-borda last:border-0">
                          <th scope="row" className={`${TD} numero text-left font-normal whitespace-nowrap`}>{`${c.ano_mes.slice(5, 7)}/${c.ano_mes.slice(0, 4)}`}</th>
                          {d.escopo.modo === 'consolidado' && <td className={`${TD} text-tinta-2`}>{d.escopo.empresas.find((e) => e.id === c.empresa_id)?.nome ?? ''}</td>}
                          <td className={TD}>
                            <Selo cor={c.estado === 'fechada' ? 'verde' : 'neutro'}>{t(`folha.estados.${c.estado}`)}</Selo>
                          </td>
                          <td className={`${TD} numero text-right`}>{c.funcionarios}</td>
                          {VALORES.map((k) => (
                            <td key={k} className={`${TD} text-right`}>
                              <Valor centavos={Number(c[k])} moeda={c.moeda} />
                            </td>
                          ))}
                          <td className={`${TD} text-right font-semibold`}>
                            <Valor centavos={Number(c.proventos_centavos) + Number(c.encargos_centavos)} moeda={c.moeda} />
                          </td>
                        </tr>
                      ))}
                      {d.totais.map((x) => (
                        <tr key={`total-${x.moeda}`} className="border-b border-borda bg-sup-2 font-semibold last:border-0">
                          <th scope="row" className={`${TD} text-left`}>{d.totais.length > 1 ? t('comum.moeda', { moeda: x.moeda }) : t('comum.total')}</th>
                          {d.escopo.modo === 'consolidado' && <td className={TD} />}
                          <td className={TD} />
                          <td className={TD} />
                          {VALORES.map((k) => (
                            <td key={k} className={`${TD} text-right`}>
                              <Valor centavos={x[k]} moeda={x.moeda} />
                            </td>
                          ))}
                          <td className={`${TD} text-right`}>
                            <Valor centavos={x.custo_centavos} moeda={x.moeda} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Rolagem>
              </Cartao>
            )}
          </>
        )}
      </Estado>
    </div>
  );
}
